import {
    ContactsList,
    ContactStatus,
    ListPayload,
    CampaignListPayload,
    RandomListPayload,
    JourneyListPayload,
    AutomationContactType,
    DeliveryLogType,
    StatusMessageCategory,
} from '@dev/ee-api/ee-api-models';

import { AxiosPromise, AxiosResponse } from 'axios';

import { request } from './request';

export const loadLists = (params?: {
    search?: string;
    includeHistory?: boolean;
    limit?: number;
    offset?: number;
    from?: string;
    to?: string;
}): AxiosPromise<ContactsList[]> => {
    const { includeHistory, limit, offset, from, to, search } = params;
    const { Lists } = request();
    return Lists.listsGet(search, includeHistory, from, to, limit, offset).then(
        (resp): AxiosResponse<ContactsList[]> => resp
    );
};

export const deleteList = (name: string): Promise<void> => {
    const { Lists } = request();
    return Lists.listsByListnameDelete(name).then((resp): void => resp.data);
};

export const addList = (body: ListPayload): AxiosPromise<ContactsList> => {
    const { Lists } = request();
    return Lists.listsPost(body);
};

export const createRandomList = (body: RandomListPayload): Promise<ContactsList> => {
    const { Lists } = request();
    return Lists.listsRandomPost(body).then((resp): ContactsList => resp.data);
};

export const createNthSelection = (listname: ContactsList): Promise<void> => {
    const { Lists } = request();
    return Lists.listsNthselectionPost(listname).then((resp): void => resp.data);
};

export const updateList = (
    listname: string,
    body: { NewListName: string; AllowUnsubscribe: boolean }
): Promise<ContactsList> => {
    const { Lists } = request();
    return Lists.listsByListnamePut(listname, body).then((resp): ContactsList => resp.data);
};

export const loadSingleList = (listname: string): Promise<ContactsList> => {
    const { Lists } = request();
    return Lists.listsByListnameGet(listname).then((resp): ContactsList => resp.data);
};

export const loadListsHistory = (params: {
    includeHistory?: boolean;
    from?: string;
    to?: string;
    options?: any;
}): AxiosPromise<ContactsList[]> => {
    const { includeHistory = true, from, to, options } = params;
    const { Lists } = request();
    return Lists.listsTrackedhistoryGet(includeHistory, from, to, options).then(
        (response): AxiosResponse<ContactsList[]> => response
    );
};

export const addContactsToList = (
    listName: string,
    body: { Emails?: string[]; AllContacts?: boolean; Rule?: string }
): Promise<ContactsList> => {
    const { Lists } = request();
    return Lists.listsByListnameContactsPut(listName, body).then((resp): ContactsList => resp.data);
};

export const loadContactLists = (email: string): Promise<ContactsList[]> => {
    const { Lists } = request();
    return Lists.listsContactsByEmailGet(email).then((resp): ContactsList[] => resp.data);
};

export const removeContactsFromList = (
    listName: string,
    body: { emails?: string[]; rule?: string }
): AxiosPromise<void> => {
    const { Lists } = request();
    return Lists.listsByListnameContactsDelete(listName, body.emails, body.rule);
};

export const moveContactsToList = (
    listName: string,
    body: { NewListName: string; Statuses?: ContactStatus[]; Emails?: string[]; AllContacts?: boolean; Rule?: string }
): Promise<ContactsList> => {
    const { Lists } = request();
    return Lists.listsByListnameContactsMovePut(listName, body).then((resp): ContactsList => resp.data);
};

export type ListFromCampaignParams = {
    campaignName: string;
    listName: string;
    statuses?: DeliveryLogType[];
    messageCategory?: StatusMessageCategory[];
    from?: string;
    to?: string;
    email?: string;
    newList: boolean;
    lastEvent?: boolean;
};

export type ListFromAutomationParams = {
    journeyName: string;
    listName: string;
    type?: AutomationContactType;
    from?: string | null;
    to?: string | null;
    newList: boolean;
};

export const listFromCampaign = (params: ListFromCampaignParams): AxiosPromise<CampaignListPayload> => {
    const { Lists } = request();
    const { campaignName, listName, statuses, messageCategory, from, to, email, newList, lastEvent } = params;

    return Lists.listsFromcampaignPost({
        CampaignName: campaignName,
        ListName: listName,
        Statuses: statuses,
        MessageCategories: messageCategory,
        From: from,
        To: to,
        Email: email,
        NewList: newList,
        LastEvent: lastEvent,
    });
};

export const listFromAutomation = (params: ListFromAutomationParams): AxiosPromise<ContactsList> => {
    const { Lists } = request();
    const { journeyName, listName, type, from, to, newList } = params;

    return Lists.listsFromautomationPost({
        JourneyName: journeyName,
        ListName: listName,
        Type: type,
        From: from,
        To: to,
        NewList: newList,
    } as JourneyListPayload);
};
